import { makeAutoObservable } from 'mobx'
import { decorate, inject, injectable } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'

const BC_AUTH_CHANNEL = 'evertel-auth'
const BC_AUTH_MESSAGES = {
    USER_LOGIN: 'user-login',
    USER_LOGOUT: 'user-logout'
} as const

type BCAuthMessageType = typeof BC_AUTH_MESSAGES[keyof typeof BC_AUTH_MESSAGES]

interface BCAuthMessage {
    type: BCAuthMessageType
    userId?: number
}

class AuthBroadcastService {
    private bc: BroadcastChannel | null = null

    constructor(
        private sessionService: SessionService
    ) {
        makeAutoObservable(this)
    }

    init = () => {
        if (this.bc) return // Already initialized

        this.bc = new BroadcastChannel(BC_AUTH_CHANNEL)
        this.bc.onmessage = this.handleMessage
    }

    private handleMessage = (event: MessageEvent<BCAuthMessage>) => {
        const { type, userId } = event.data
        const currentUserId = this.sessionService.currentUserId

        if (type === BC_AUTH_MESSAGES.USER_LOGIN) {
            if (!currentUserId) {
                // Tab was on login page, user logged in on another tab - reload to pick up auth state
                window.location.reload()
            } else if (currentUserId !== userId) {
                // Different user logged in on another tab - reload to pick up new state
                window.location.reload()
            }
        }

        if (type === BC_AUTH_MESSAGES.USER_LOGOUT) {
            if (currentUserId) {
                // Another tab logged out - reload to re-evaluate auth state
                window.location.reload()
            }
        }
    }

    broadcastLogin = (userId: number) => {
        this.bc?.postMessage({ type: BC_AUTH_MESSAGES.USER_LOGIN, userId })
    }

    broadcastLogout = () => {
        this.bc?.postMessage({ type: BC_AUTH_MESSAGES.USER_LOGOUT })
    }

    destroy = () => {
        this.bc?.close()
        this.bc = null
    }
}

decorate(injectable(), AuthBroadcastService)
decorate(inject(SessionService), AuthBroadcastService, 0)

export { AuthBroadcastService }
